import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import type { Observable } from 'rxjs';
import { switchMap, map, catchError, throwError, forkJoin, tap, of } from 'rxjs';
import config from '../../../config';

/**
 * SharePoint token response interface
 */
interface SharePointTokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
}

/**
 * SharePoint file upload response interface
 */
interface SharePointUploadResponse {
  name: string;
  size: number;
  webUrl: string;
  id: string;
}

/**
 * Service for handling SharePoint file uploads
 * Uploads activity attachments to SharePoint document library
 */
@Injectable({
  providedIn: 'root'
})
export class SharePointService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${config.API_GATEWAY_ENV_ENDPOINT}`;

  // SharePoint site configuration
  private readonly SHAREPOINT_SITE_ID = '215a2dbb-830a-4dfd-ad68-1bbb956b6d0b';
  private readonly SHAREPOINT_FOLDER_PATH = '/befair';

  // Token caching
  private accessToken: string | null = null;
  private tokenExpiration: number | null = null;

  /**
   * Get SharePoint access token from backend or cache
   * Automatically refreshes token if expired
   */
  private getSharePointToken(): Observable<string> {
    // Check if we have a valid cached token
    if (this.accessToken && this.tokenExpiration && Date.now() < this.tokenExpiration) {
      return of(this.accessToken);
    }

    // Fetch new token
    return this.http.get<SharePointTokenResponse>(`${this.apiUrl}/sharepoint-token`)
      .pipe(
        tap(response => {
          // Cache the token and calculate expiration time
          this.accessToken = response.access_token;
          // Subtract 5 minutes (300 seconds) as a buffer before actual expiration
          const expiresInMs = (response.expires_in - 300) * 1000;
          this.tokenExpiration = Date.now() + expiresInMs;
        }),
        map(response => response.access_token),
        catchError(error => {
          console.error('Error fetching SharePoint token:', error);
          // Clear cached token on error
          this.accessToken = null;
          this.tokenExpiration = null;
          return throwError(() => new Error('Failed to retrieve SharePoint access token'));
        })
      );
  }

  /**
   * Clear cached token (useful for testing or manual token refresh)
   */
  clearToken(): void {
    this.accessToken = null;
    this.tokenExpiration = null;
  }

  /**
   * Upload a file to SharePoint
   * @param file - The file to upload
   * @param folderPath - The folder path within the SharePoint site (optional, uses default if not provided)
   * @param fileName - Optional custom file name (uses original if not provided)
   * @returns Observable with upload response containing file details
   */
  uploadFile(file: File, folderPath?: string, fileName?: string): Observable<SharePointUploadResponse> {
    const targetFolder = folderPath || this.SHAREPOINT_FOLDER_PATH;
    const uploadFileName = fileName || file.name;

    return this.getSharePointToken().pipe(
      switchMap(accessToken => {
        // Construct the SharePoint Graph API URL for file upload
        // Using Microsoft Graph API to upload file to SharePoint
        const uploadUrl = `https://graph.microsoft.com/v1.0/sites/${this.SHAREPOINT_SITE_ID}/drive/root:${targetFolder}/${uploadFileName}:/content`;

        const headers = new HttpHeaders({
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': file.type || 'application/octet-stream'
        });

        return this.http.put<SharePointUploadResponse>(uploadUrl, file, { headers }).pipe(
          catchError(error => {
            console.error('Error uploading file to SharePoint:', error);
            return throwError(() => new Error(`Failed to upload file: ${uploadFileName}`));
          })
        );
      })
    );
  }

  /**
   * Upload multiple files to SharePoint
   * @param files - Array of files with their metadata
   * @returns Observable with array of upload responses
   */
  uploadMultipleFiles(files: { file: File; folderPath?: string; fileName?: string }[]): Observable<SharePointUploadResponse[]> {
    return this.getSharePointToken().pipe(
      switchMap(accessToken => {
        const uploadPromises = files.map(({ file, folderPath, fileName }) => {
          const targetFolder = folderPath || this.SHAREPOINT_FOLDER_PATH;
          const uploadFileName = fileName || file.name;
          const uploadUrl = `https://graph.microsoft.com/v1.0/sites/${this.SHAREPOINT_SITE_ID}/drive/root:${targetFolder}/${uploadFileName}:/content`;

          const headers = new HttpHeaders({
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': file.type || 'application/octet-stream'
          });

          return this.http.put<SharePointUploadResponse>(uploadUrl, file, { headers });
        });

        return forkJoin(uploadPromises);
      }),
      catchError(error => {
        console.error('Error uploading multiple files to SharePoint:', error);
        return throwError(() => new Error('Failed to upload one or more files'));
      })
    );
  }

  /**
   * Generate a timestamped file name for activity attachments
   * @param originalFileName - The original file name
   * @returns Formatted file name with numeric timestamp
   */
  generateActivityFileName(
    originalFileName: string
  ): string {
    const timestamp = Date.now();
    return `${timestamp}_${originalFileName}`;
  }

  /**
   * Get the folder path for a specific cycle and form
   * @returns Folder path
   */
  getCycleFolderPath(): string {
    return `${this.SHAREPOINT_FOLDER_PATH}`;
  }

  /**
   * Download a file from SharePoint
   * @param folderPath - The folder path within the SharePoint site
   * @param fileName - The file name to download
   * @returns Observable with blob data
   */
  downloadFile(folderPath: string, fileName: string): Observable<Blob> {
    return this.getSharePointToken().pipe(
      switchMap(accessToken => {
        // Construct the SharePoint Graph API URL for file download
        const downloadUrl = `https://graph.microsoft.com/v1.0/sites/${this.SHAREPOINT_SITE_ID}/drive/root:${folderPath}/${fileName}:/content`;

        const headers = new HttpHeaders({
          'Authorization': `Bearer ${accessToken}`
        });

        return this.http.get(downloadUrl, { headers, responseType: 'blob' }).pipe(
          catchError(error => {
            console.error('Error downloading file from SharePoint:', error);
            return throwError(() => new Error(`Failed to download file: ${fileName}`));
          })
        );
      })
    );
  }

  /**
   * Download file by URL
   * @param fileUrl - The SharePoint web URL of the file
   * @returns Observable with blob data
   */
  downloadFileByUrl(fileUrl: string): Observable<Blob> {
    return this.getSharePointToken().pipe(
      switchMap(accessToken => {
        // Extract the file path from the web URL and construct download URL
        // Convert web URL to Graph API content URL
        const downloadUrl = fileUrl.replace('/sites/', '/v1.0/sites/')
          .replace('/_layouts/15/Doc.aspx?sourcedoc=', '/drive/items/')
          + '/content';

        const headers = new HttpHeaders({
          'Authorization': `Bearer ${accessToken}`
        });

        return this.http.get(downloadUrl, { headers, responseType: 'blob' }).pipe(
          catchError(error => {
            console.error('Error downloading file from SharePoint:', error);
            return throwError(() => new Error('Failed to download file'));
          })
        );
      })
    );
  }
}
